/* Shoppable video carousel — landscape 16:9, center-focused, swipeable.
   Custom element <shoppable-video-carousel>. Upgrades automatically when
   the section is added/reloaded in the theme editor. Self-scoped so
   multiple instances on one page work independently. */
(function () {
  if (customElements.get('shoppable-video-carousel')) return;

  function loadYouTubeApi() {
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
    if (window.__svcYouTubeApiPromise) return window.__svcYouTubeApiPromise;

    window.__svcYouTubeApiPromise = new Promise((resolve, reject) => {
      const previousReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        try {
          if (typeof previousReady === 'function') previousReady();
        } finally {
          resolve(window.YT);
        }
      };

      let script = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('error', reject, { once: true });
    });

    return window.__svcYouTubeApiPromise;
  }

  function loadVimeoApi() {
    if (window.Vimeo && window.Vimeo.Player) return Promise.resolve(window.Vimeo);
    if (window.__svcVimeoApiPromise) return window.__svcVimeoApiPromise;

    window.__svcVimeoApiPromise = new Promise((resolve, reject) => {
      let script = document.querySelector('script[src="https://player.vimeo.com/api/player.js"]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', () => resolve(window.Vimeo), { once: true });
      script.addEventListener('error', reject, { once: true });
    });

    return window.__svcVimeoApiPromise;
  }

  class ShoppableVideoCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.svc__track');
      this.items = Array.from(this.querySelectorAll('.svc__item'));
      if (!this.track || !this.items.length) return;

      this.prevBtn = this.querySelector('.svc__nav--prev');
      this.nextBtn = this.querySelector('.svc__nav--next');
      this.activeIndex = -1;
      this._ticking = false;

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onKeydown = this._onKeydown.bind(this);

      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this.track.addEventListener('keydown', this._onKeydown);
      window.addEventListener('resize', this._onResize);

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.thumbsWrap = this.querySelector('.svc__thumbs');
      this.thumbs = this.thumbsWrap
        ? Array.from(this.thumbsWrap.querySelectorAll('.svc__thumb'))
        : [];
      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => {
          if (i !== this.activeIndex) this.center(i, true);
        });
      });

      this._setupTips();

      this.items.forEach((item, i) => {
        item._svcPlaying = false;
        item._svcSoundOn = false;
        item._svcUserPaused = false;
        item._svcPlayer = null;
        item._svcPlayerReady = false;

        item.addEventListener('click', (e) => {
          if (e.target.closest('.svc__controls') || e.target.closest('.svc__products')) return;
          if (i !== this.activeIndex) {
            this.center(i, true);
          } else {
            this.togglePlayback(i);
          }
        });

        const playback = item.querySelector('.svc__playback');
        if (playback) {
          playback.addEventListener('click', (e) => {
            e.stopPropagation();
            this.togglePlayback(i);
          });
        }

        const sound = item.querySelector('.svc__sound');
        if (sound) {
          sound.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleSound(i);
          });
        }

        const media = this.mediaFor(item);
        if (media && media.tagName === 'VIDEO') {
          media.addEventListener('play', () => this.setPlaybackUi(item, true));
          media.addEventListener('pause', () => this.setPlaybackUi(item, false));
          media.addEventListener('ended', () => this.setPlaybackUi(item, false));
        } else if (media && media.tagName === 'IFRAME') {
          this.setupEmbedPlayer(item, media);
        }

        this.setPlaybackUi(item, false);
        this.setSoundUi(item, false);
      });

      // Stage the middle clip on load.
      const start = Math.floor(this.items.length / 2);
      requestAnimationFrame(() => {
        this.center(start, false);
      });
    }

    disconnectedCallback() {
      if (this.track) {
        this.track.removeEventListener('scroll', this._onScroll);
        this.track.removeEventListener('keydown', this._onKeydown);
      }
      window.removeEventListener('resize', this._onResize);
      if (this._tipScrollHide) {
        if (this.thumbsWrap) this.thumbsWrap.removeEventListener('scroll', this._tipScrollHide);
        window.removeEventListener('scroll', this._tipScrollHide);
        window.removeEventListener('resize', this._tipScrollHide);
      }
      if (this._tip && this._tip.parentNode) {
        this._tip.parentNode.removeChild(this._tip);
        this._tip = null;
      }
    }

    nearestToCenter() {
      const tr = this.track.getBoundingClientRect();
      const center = tr.left + tr.width / 2;
      let best = 0;
      let bestDist = Infinity;
      this.items.forEach((item, i) => {
        const r = item.getBoundingClientRect();
        const dist = Math.abs(r.left + r.width / 2 - center);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    _onScroll() {
      if (this._ticking) return;
      this._ticking = true;
      requestAnimationFrame(() => {
        this.setActive(this.nearestToCenter());
        this._ticking = false;
      });
    }

    _onResize() {
      if (this.activeIndex >= 0) this.center(this.activeIndex, false);
    }

    _onKeydown(e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.step(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.step(-1);
      }
    }

    step(dir) {
      const from = this.activeIndex < 0 ? 0 : this.activeIndex;
      const next = Math.min(this.items.length - 1, Math.max(0, from + dir));
      this.center(next, true);
    }

    center(i, smooth) {
      const item = this.items[i];
      if (!item) return;
      this.setActive(i);
      requestAnimationFrame(() => {
        const left = item.offsetLeft - (this.track.clientWidth - item.clientWidth) / 2;
        this.track.scrollTo({ left: left, behavior: smooth && !this.reduceMotion.matches ? 'smooth' : 'auto' });
      });
    }

    revealThumb(i) {
      // Keep the active thumb in view. Manual scroll math instead of
      // scrollIntoView, which can also scroll the page vertically.
      const wrap = this.thumbsWrap;
      const thumb = this.thumbs[i];
      if (!wrap || !thumb || wrap.scrollWidth <= wrap.clientWidth) return;
      const left = thumb.offsetLeft - (wrap.clientWidth - thumb.clientWidth) / 2;
      wrap.scrollTo({ left: left, behavior: this.reduceMotion.matches ? 'auto' : 'smooth' });
    }

    _setupTips() {
      // Black title box shown on hover/focus of thumbnails and product bag
      // buttons. Gated to hover-capable pointers — touch devices can't hover
      // and keep their tap-to-scroll, with no tooltip flicker on tap-focus.
      const triggers = [
        ...this.thumbs,
        ...this.querySelectorAll('.shoppable-video__add-button'),
      ];
      if (!triggers.length) return;
      if (!window.matchMedia('(hover: hover)').matches) return;

      const tip = document.createElement('div');
      tip.className = 'svc__tip';
      tip.setAttribute('aria-hidden', 'true');
      document.body.appendChild(tip);
      this._tip = tip;

      this._hideTip = this._hideTip.bind(this);
      // A position:fixed box goes stale the instant anything scrolls; hide it.
      this._tipScrollHide = () => this._hideTip();

      triggers.forEach((el) => {
        const show = () => this._showTip(el);
        el.addEventListener('mouseenter', show);
        el.addEventListener('focus', show);
        el.addEventListener('mouseleave', this._hideTip);
        el.addEventListener('blur', this._hideTip);
      });

      if (this.thumbsWrap) {
        this.thumbsWrap.addEventListener('scroll', this._tipScrollHide, { passive: true });
      }
      window.addEventListener('scroll', this._tipScrollHide, { passive: true });
      window.addEventListener('resize', this._tipScrollHide);
    }

    _showTip(el) {
      const tip = this._tip;
      if (!tip) return;
      const title =
        el.getAttribute('data-svc-thumb-title') || el.getAttribute('data-svc-tip');
      if (!title) return;
      tip.textContent = title;

      const r = el.getBoundingClientRect();
      const margin = 8;
      // Reveal first so offsetWidth is measurable for the viewport clamp.
      tip.classList.add('is-visible');
      const half = tip.offsetWidth / 2;
      const triggerCenter = r.left + r.width / 2;
      const clampedX = Math.min(
        Math.max(triggerCenter, margin + half),
        window.innerWidth - margin - half
      );
      tip.style.left = clampedX + 'px';
      tip.style.top = r.top - 10 + 'px';
      // Keep the caret under the trigger's true centre even if the box was clamped.
      const caretX = Math.min(Math.max(triggerCenter - (clampedX - half), 12), half * 2 - 12);
      tip.style.setProperty('--svc-tip-caret-x', caretX + 'px');
    }

    _hideTip() {
      if (this._tip) this._tip.classList.remove('is-visible');
    }

    mediaFor(item) {
      return item && item.querySelector('.svc__video');
    }

    setupEmbedPlayer(item, media) {
      const provider = media.dataset.svcProvider;
      item._svcProvider = provider;

      if (provider === 'youtube') {
        loadYouTubeApi()
          .then((YT) => {
            if (!this.isConnected || !media.isConnected) return;
            item._svcPlayer = new YT.Player(media, {
              events: {
                onReady: (event) => {
                  item._svcPlayer = event.target;
                  item._svcPlayerReady = true;
                  if (item._svcSoundOn) event.target.unMute();
                  else event.target.mute();
                  this.updatePlayback();
                },
                onStateChange: (event) => {
                  if (event.data === YT.PlayerState.PLAYING) {
                    this.setPlaybackUi(item, true);
                  } else if (
                    event.data === YT.PlayerState.PAUSED ||
                    event.data === YT.PlayerState.ENDED ||
                    event.data === YT.PlayerState.CUED
                  ) {
                    this.setPlaybackUi(item, false);
                  }
                },
                onAutoplayBlocked: () => this.setPlaybackUi(item, false),
                onError: () => this.setPlaybackUi(item, false),
              },
            });
          })
          .catch(() => this.setPlaybackUi(item, false));
      } else if (provider === 'vimeo') {
        loadVimeoApi()
          .then((Vimeo) => {
            if (!this.isConnected || !media.isConnected) return;
            const player = new Vimeo.Player(media);
            item._svcPlayer = player;
            player.on('play', () => this.setPlaybackUi(item, true));
            player.on('pause', () => this.setPlaybackUi(item, false));
            player.on('ended', () => this.setPlaybackUi(item, false));
            player.on('volumechange', (state) => {
              this.setSoundUi(item, !state.muted && state.volume > 0);
            });
            return player.ready().then(() => {
              item._svcPlayerReady = true;
              const soundUpdate = player.setMuted(!item._svcSoundOn);
              if (soundUpdate && typeof soundUpdate.catch === 'function') {
                soundUpdate.catch(function () {});
              }
              this.updatePlayback();
            });
          })
          .catch(() => this.setPlaybackUi(item, false));
      }
    }

    setPlaybackUi(item, isPlaying) {
      if (!item) return;
      item._svcPlaying = isPlaying;
      const button = item.querySelector('.svc__playback');
      if (!button) return;
      const title = button.dataset.svcTitle || 'video';
      const action = isPlaying ? 'Pause' : 'Play';
      button.dataset.playing = String(isPlaying);
      button.setAttribute('aria-label', action + ' video: ' + title);
    }

    setSoundUi(item, isOn) {
      if (!item) return;
      item._svcSoundOn = isOn;
      const button = item.querySelector('.svc__sound');
      if (!button) return;
      const title = button.dataset.svcTitle || 'video';
      const action = isOn ? 'Mute' : 'Unmute';
      button.dataset.sound = isOn ? 'on' : 'off';
      button.setAttribute('aria-pressed', String(isOn));
      button.setAttribute('aria-label', action + ' video: ' + title);
    }

    playItem(item) {
      const media = this.mediaFor(item);
      if (!media) return;

      if (media.tagName === 'VIDEO') {
        this.setPlaybackUi(item, true);
        const playPromise = media.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => this.setPlaybackUi(item, false));
        }
      } else {
        this.setPlaybackUi(item, true);
        if (!item._svcPlayerReady || !item._svcPlayer) return;
        if (item._svcProvider === 'youtube') {
          item._svcPlayer.playVideo();
        } else if (item._svcProvider === 'vimeo') {
          const playPromise = item._svcPlayer.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => this.setPlaybackUi(item, false));
          }
        }
      }
    }

    pauseItem(item) {
      const media = this.mediaFor(item);
      if (!media) return;

      if (media.tagName === 'VIDEO') {
        if (!media.paused) media.pause();
        else this.setPlaybackUi(item, false);
      } else {
        this.setPlaybackUi(item, false);
        if (!item._svcPlayerReady || !item._svcPlayer) return;
        if (item._svcProvider === 'youtube') {
          item._svcPlayer.pauseVideo();
        } else if (item._svcProvider === 'vimeo') {
          const pausePromise = item._svcPlayer.pause();
          if (pausePromise && typeof pausePromise.catch === 'function') {
            pausePromise.catch(function () {});
          }
        }
      }
    }

    muteItem(item) {
      const media = this.mediaFor(item);
      if (!media) return;

      this.setSoundUi(item, false);
      if (media.tagName === 'VIDEO') {
        media.muted = true;
      } else if (item._svcPlayerReady && item._svcPlayer) {
        if (item._svcProvider === 'youtube') {
          item._svcPlayer.mute();
        } else if (item._svcProvider === 'vimeo') {
          const mutePromise = item._svcPlayer.setMuted(true);
          if (mutePromise && typeof mutePromise.catch === 'function') {
            mutePromise.catch(function () {});
          }
        }
      }
    }

    unmuteItem(item) {
      const media = this.mediaFor(item);
      if (!media) return;

      this.setSoundUi(item, true);
      if (media.tagName === 'VIDEO') {
        media.muted = false;
      } else if (item._svcPlayerReady && item._svcPlayer) {
        if (item._svcProvider === 'youtube') {
          item._svcPlayer.unMute();
        } else if (item._svcProvider === 'vimeo') {
          const unmutePromise = item._svcPlayer.setMuted(false);
          if (unmutePromise && typeof unmutePromise.catch === 'function') {
            unmutePromise.catch(() => this.setSoundUi(item, false));
          }
        }
      }
    }

    togglePlayback(i) {
      const item = this.items[i];
      if (!item) return;

      if (item._svcPlaying) {
        item._svcUserPaused = true;
        this.pauseItem(item);
      } else {
        item._svcUserPaused = false;
        this.playItem(item);
      }
    }

    setActive(idx) {
      if (idx === this.activeIndex) {
        this.updatePlayback();
        return;
      }
      this.activeIndex = idx;
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === idx));
      this.thumbs.forEach((thumb, i) => {
        const on = i === idx;
        thumb.classList.toggle('is-active', on);
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.revealThumb(idx);
      // Re-mute every clip whenever the active stage changes.
      this.items.forEach((item) => {
        item._svcUserPaused = false;
        this.muteItem(item);
      });
      this.updatePlayback();
    }

    updatePlayback() {
      // When the section is hidden (e.g. "Hide on mobile" => display:none),
      // offsetParent is null; pause everything so nothing buffers off-screen.
      const hidden = this.offsetParent === null;
      this.items.forEach((item, i) => {
        const media = this.mediaFor(item);
        if (!media) return;
        const isNative = media.tagName === 'VIDEO';
        const inPlaybackRange = isNative
          ? Math.abs(i - this.activeIndex) <= 1
          : i === this.activeIndex;
        const shouldPlay = !hidden && inPlaybackRange && !item._svcUserPaused;
        if (shouldPlay) this.playItem(item);
        else this.pauseItem(item);
      });
    }

    toggleSound(i) {
      const item = this.items[i];
      if (!this.mediaFor(item)) return;
      const turnOn = !item._svcSoundOn;
      // Solo: mute every other clip.
      this.items.forEach((other) => {
        if (other === item) return;
        this.muteItem(other);
      });
      if (turnOn) this.unmuteItem(item);
      else this.muteItem(item);
    }
  }

  customElements.define('shoppable-video-carousel', ShoppableVideoCarousel);
})();
